import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  reload,
  loadPage,
  setActiveGap,
  setStaffInCharge,
  setClient,
  setDate,
  setEndDate,
  setTextFilter,
  setShowHiddenReasons,
  sendReminders,
  deleteRecordComment,
} from "./gapsSlice";
import ActionsTable from "../../components/templates/ActionsTable";
import Surface from "../../components/layouts/Surface";
import { ReactComponent as BranchesImg } from "../../assets/no-branches.svg";
import { getgapsColumns } from "./components/columns";
import Header from "./components/Header";
import SchedulingModal from "./components/SchedulingModal";
import { loadRecord, reset } from "../employeeDetails/employeeDetailsSlice";
import {
  loadRecord as loadPatient,
  reset as resetPatient,
} from "../patientDetails/patientDetailsSlice";
import { reset as resetAbsences } from "../employeeAbsences/employeeAbsencesSlice";
import { reset as resetScheduling } from "../employeeScheduling/schedulingSlice";
import moment from "moment";
import useDebounce from "../../components/util/useDebounce";
import ApprovalModal from "../employees/components/ApprovalModal";
import EditCommentModal from "./components/EditCommentModal";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { usePermission } from "../../hooks/accessControl";
import PatientApprovalWrapper from "../../features/scheduling/components/PatientApprovalWrapper";
import OverlapsTable from "./components/OverlapsTable";

const Gaps = (props) => {
  const { diariesApproval } = props;
  const [modal, contextHolder] = Modal.useModal();
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const dispatch = useDispatch();
  let location = useLocation();
  const { page: gapsPage } = useSelector((state) => state.gaps);
  const activeGap = useSelector((state) => state.gaps.activeGap);
  const staffId = useSelector((state) => state.gaps.staffId);
  const clientId = useSelector((state) => state.gaps.clientId);
  const activeYear = useSelector((state) => state.gaps.activeYear);
  const activeDate = useSelector((state) => state.gaps.activeDate);
  const effective_to = useSelector((state) => state.gaps.effective_to);
  const showHiddenReasons = useSelector(
    (state) => state.gaps.show_hidden_reasons
  );
  const { current, pageSize, total, items, filter, order } = gapsPage;
  const loading = useSelector((state) => state.pendingActions["gaps/loadPage"]);
  const [schedulingModalVisible, setSchedulingModalVisible] = useState();
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [patientApprovalModalVisible, setPatientApprovalModalVisible] =
    useState(false);

  const [editCommentRecord, setEditCommentRecord] = useState(null);
  const [editCommentType, setEditCommentType] = useState(null);

  const intl = useIntl();
  let { branchId } = useParams();
  const debouncedSearch = useDebounce(filter.freeText, 400);

  useEffect(() => {
    if (activeGap) {
      dispatch(loadPage());
    }
  }, [
    dispatch,
    activeGap,
    staffId,
    clientId,
    activeDate,
    effective_to,
    activeYear,
    showHiddenReasons,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (diariesApproval) {
      dispatch(setActiveGap({ gap: "unapproved_minutes" }));

      var dayInMonth = +moment().format("DD");
      if (dayInMonth < 20) {
        //show last month
        dispatch(
          setDate({
            date: moment().subtract(1, "month").startOf("month").format(),
          })
        );
      } else {
        //show current month
        dispatch(setDate({ date: moment().startOf("month").format() }));
      }
    } else {
      if (activeGap === "unapproved_minutes" || !activeGap) {
        dispatch(setActiveGap({ gap: "lack_of_extraction_of_hours" }));
      } else if (activeGap === "ok2go") {
        dispatch(setActiveGap({ gap: "ok2go" }));
      } else if (activeGap === "overlaps") {
        dispatch(setActiveGap({ gap: "overlaps" }));
      }
    }
  }, [dispatch, diariesApproval, activeGap]);

  useEffect(() => {
    return () => {
      dispatch(setActiveGap({ gap: null }));
    };
  }, [dispatch]);

  const getPatient = (id) => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      id: id,
      branchId: branchId,
      admin: isAdmin,
    };
    return dispatch(loadPatient(params));
  };

  const getEmployee = (id) => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      id: id,
      branchId: branchId,
      admin: isAdmin,
    };
    dispatch(loadRecord(params));
  };

  const reloadGaps = () => {
    dispatch(loadPage());
  };

  const onDateChange = (date, dateString) => {
    var gapsDate = date ? date.format() : null;
    dispatch(setDate({ date: gapsDate }));
  };

  const onEndDateChange = (date, dateString) => {
    var gapsDate = date ? date.endOf("day").format() : undefined;
    dispatch(setEndDate({ effective_to: gapsDate }));
  };

  const onGapChange = (val) => {
    dispatch(setActiveGap({ gap: val }));
  };

  const onStaffChange = (val) => {
    dispatch(setStaffInCharge({ staffId: val }));
  };

  const onClientChange = (val) => {
    dispatch(setClient({ clientId: val }));
  };

  const onTextChange = (e) => {
    dispatch(setTextFilter({ text: e.target.value }));
  };

  const onShowHiddenReasonsChange = (e) => {
    dispatch(setShowHiddenReasons({ show_hidden_reasons: e.target.checked }));
  };

  const onlaunchPatientScheduler = async (record) => {
    await getPatient(record.order.patient_id);
    setSchedulingModalVisible(true);
  };

  const onHideSchedulingModal = () => {
    setSchedulingModalVisible(false);
    reloadGaps();
    dispatch(resetPatient());
  };

  const onlaunchEditCommentModal = async (record, type) => {
    setEditCommentRecord(record);
    setEditCommentType(type);
  };

  const onDeleteGapComment = (record) => {
    const title = intl.formatMessage({
      id: "app.gaps.deleteGapCommentTitle",
    });
    const content = intl.formatMessage({
      id: "app.gaps.deleteGapCommentContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirmDelete(record);
      },
      onCancel() {},
    });
  };

  const onConfirmDelete = async (record) => {
    const params = {
      order_gap_reason_comment_id: record.order_gap_reason_comment_id,
      scopePrefix,
    };
    const action = await dispatch(deleteRecordComment(params));
    if (!action.error) {
      dispatch(
        loadPage({
          current: 1,
        })
      );
    }
  };

  const onlaunchApprovalModal = async (record) => {
    await getEmployee(record.employee_id);
    setApprovalModalVisible(true);
  };

  const onlaunchPatientApprovalModal = async (record) => {
    await getPatient(record.order.patient_id);
    setPatientApprovalModalVisible(true);
  };

  const onHidePatientApprovalModal = () => {
    setPatientApprovalModalVisible(false);
    reloadGaps();
  };

  const hideApprovalModal = () => {
    setApprovalModalVisible(false);
    reloadGaps();
    dispatch(resetScheduling());
    dispatch(resetAbsences());
    dispatch(reset());
  };

  const onSendSms = () => {
    const title = intl.formatMessage({
      id: "app.gaps.sendSmsTitle",
    });
    const content = `${intl.formatMessage({
      id: "app.gaps.sendSmsContent1",
    })} ${total} ${intl.formatMessage({
      id: "app.gaps.sendSmsContent2",
    })}`;

    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return dispatch(sendReminders());
      },
      onCancel() {},
    });
  };
  const hasEditorPermission = usePermission(branchId, "gaps", "editor");

  return (
    <Surface>
      {contextHolder}
      <Header
        activeDate={activeDate}
        effective_to={effective_to}
        onDateChange={onDateChange}
        onEndDateChange={onEndDateChange}
        activeGap={activeGap}
        onGapChange={onGapChange}
        onStaffChange={onStaffChange}
        onClientChange={onClientChange}
        reload={reloadGaps}
        onTextChange={onTextChange}
        onShowHiddenReasonsChange={onShowHiddenReasonsChange}
        showHiddenReasons={showHiddenReasons}
        onSendSms={onSendSms}
        textFilter={filter.freeText}
        branchId={branchId}
        diariesApproval={diariesApproval}
      />
      <div style={{ height: "10px" }} />
      {activeGap === "overlaps" ? (
        <OverlapsTable
          data={items}
          loading={loading}
          onlaunchApprovalModal={onlaunchApprovalModal}
          total={total}
        />
      ) : (
        <ActionsTable
          deep={true}
          tableItems={getgapsColumns(
            activeGap,
            intl,
            location,
            onlaunchPatientScheduler,
            onlaunchApprovalModal,
            onlaunchPatientApprovalModal,
            onlaunchEditCommentModal,
            onDeleteGapComment,
            hasEditorPermission
          )}
          category={"gaps"}
          resource="gaps"
          order={order}
          current={current}
          pageSize={pageSize}
          total={total}
          loading={loading}
          textFilter={filter.freeText}
          reload={reload}
          loadPage={loadPage}
          skipInitialLoad={true}
          // resetPage={resetPage}
          items={items.map((i, index) => ({ ...i, id: index }))}
          showheader={false}
          linkType={"branches"}
          emptyImage={<BranchesImg />}
          emptytext={<FormattedMessage id="app.tables.noGaps" />}
          scroll={window.innerHeight - 406}
        />
      )}
      <SchedulingModal
        onHide={onHideSchedulingModal}
        visible={schedulingModalVisible}
        initialDate={activeDate}
        showPatientDetails={true}
      />
      <ApprovalModal
        initialDate={activeDate}
        onHide={hideApprovalModal}
        visible={approvalModalVisible}
      />
      <PatientApprovalWrapper
        activeDate={activeDate}
        approvalModalVisible={patientApprovalModalVisible}
        onHideApprovalModal={onHidePatientApprovalModal}
      />
      <EditCommentModal
        record={editCommentRecord}
        onReloadGaps={reloadGaps}
        hide={() => {
          setEditCommentRecord(null);
          setEditCommentRecord(null);
        }}
        type={editCommentType}
      />
    </Surface>
  );
};

export default Gaps;
